import React,{useEffect,useId,useRef,useState} from 'react';
import {Check,ChevronDown,Globe,X} from 'lucide-react';
import {languages,useCareerLanguage} from './CareerLanguage';
import './CareerLanguageMenu.css';

// Navbar only: the compact native picker in the authentication forms stays unchanged.
export default function CareerLanguageMenu({onOpenChange}){
  const {lang,t,change}=useCareerLanguage();
  const entries=Object.entries(languages),selected=entries.findIndex(([code])=>code===lang);
  const [open,setOpen]=useState(false),[focused,setFocused]=useState(selected),[error,setError]=useState(false);
  const root=useRef(null),trigger=useRef(null),options=useRef([]),saveRevision=useRef(0),id=useId();
  const close=(restore=false)=>{setOpen(false);if(restore)trigger.current?.focus();};
  const show=(index=selected)=>{setFocused(index);setOpen(true);};
  useEffect(()=>{onOpenChange?.(open);},[open,onOpenChange]);
  useEffect(()=>()=>{saveRevision.current++;},[]);
  useEffect(()=>{
    if(!open)return;
    options.current[focused]?.focus();
  },[open,focused]);
  useEffect(()=>{
    if(!open)return;
    const outside=e=>{if(!root.current?.contains(e.target))setOpen(false);};
    const escape=e=>{if(e.key==='Escape'){e.preventDefault();setOpen(false);if(root.current?.contains(document.activeElement))trigger.current?.focus();}};
    document.addEventListener('pointerdown',outside);document.addEventListener('keydown',escape);
    return()=>{document.removeEventListener('pointerdown',outside);document.removeEventListener('keydown',escape);};
  },[open]);
  async function choose(code){
    const revision=++saveRevision.current;setError(false);close(true);
    try{await change(code);}catch{if(revision===saveRevision.current)setError(true);}
  }
  const keyboard=(e,index)=>{
    const steps={ArrowRight:1,ArrowLeft:-1,ArrowDown:2,ArrowUp:-2};
    let next;
    if(e.key==='Home')next=0;else if(e.key==='End')next=entries.length-1;
    else if(Object.prototype.hasOwnProperty.call(steps,e.key))next=(index+steps[e.key]+entries.length)%entries.length;
    if(next!==undefined){e.preventDefault();setFocused(next);}
  };
  const notice=<div className="clm-notice" role="alert"><span>{t('error')}</span><button type="button" onClick={()=>choose(lang)}>{t('retry')}</button></div>;
  return <div ref={root} className="clm" onBlur={e=>{if(e.relatedTarget&&!e.currentTarget.contains(e.relatedTarget))setOpen(false);}}>
    <button ref={trigger} type="button" className="clm-trigger" aria-label={t('language')+': '+languages[lang]} aria-haspopup="listbox" aria-expanded={open} aria-controls={open?id:undefined} onClick={()=>open?close():show()} onKeyDown={e=>{if(e.key==='ArrowDown'||e.key==='ArrowUp'){e.preventDefault();show();}}}>
      <Globe size={18} aria-hidden="true"/><span className="clm-current" lang={lang}>{languages[lang]}</span><span className="clm-code" aria-hidden="true">{lang.toUpperCase()}</span><ChevronDown size={14} className={open?'is-open':''} aria-hidden="true"/>
    </button>
    {open&&<div className="clm-panel">
      <header className="clm-heading"><span><Globe size={16} aria-hidden="true"/>{t('language')}</span><button type="button" aria-label={t('close')} onClick={()=>close(true)}><X size={18}/></button></header>
      <div id={id} role="listbox" aria-label={t('language')} className="clm-options">
        {entries.map(([code,name],index)=><button key={code} ref={node=>{options.current[index]=node;}} type="button" role="option" aria-selected={lang===code} tabIndex={focused===index?0:-1} className="clm-option" onFocus={()=>setFocused(index)} onKeyDown={e=>keyboard(e,index)} onClick={()=>choose(code)}>
          <span className="clm-language-code" aria-hidden="true">{code.toUpperCase()}</span><span lang={code}>{name}</span>{lang===code&&<Check size={16} aria-hidden="true"/>}
        </button>)}
      </div>
      {error&&notice}
    </div>}
    {error&&!open&&<div className="clm-panel clm-error-panel">{notice}<button type="button" className="clm-dismiss" aria-label={t('close')} onClick={()=>setError(false)}><X size={16}/></button></div>}
  </div>;
}
